<script setup lang="ts">
import {
  BarElement,
  CategoryScale,
  type ChartData,
  type ChartOptions,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
  type TooltipItem,
} from 'chart.js'
import { computed, onMounted, ref, watch } from 'vue'
import { Bar } from 'vue-chartjs'
import { useI18n } from 'vue-i18n'

import type {
  PersonalDashboard,
  PersonalDashboardActivity,
  PersonalDashboardDeadline,
} from '@/types'

import { toAppColor } from '@/composables/useAppColor'
import { eventService } from '@/services/eventService'

definePage({
  meta: {
    requiresAuth: true,
    requiresInternalAccess: true,
  },
})

ChartJS.register(CategoryScale, LinearScale, BarElement, Legend, Tooltip)

const { t, d, locale } = useI18n()
const { selectedSemester } = useMetadata()
const { enableSemesterSelector, setPageTitle } = useBreadcrumbs()
const { primary, isDarkMode } = useLayout()

const { activityRange, activityHours } = useActivityRange()

const dashboard = ref<PersonalDashboard | null>(null)
const isLoading = ref(false)
const error = ref<string | null>(null)

const numberFormatter = computed(() => new Intl.NumberFormat(locale.value))

const semesterLabel = computed(() => {
  if (!selectedSemester.value) {
    return t('pages.dashboard.allTerms')
  }

  return locale.value.startsWith('de')
    ? selectedSemester.value.nameDE
    : selectedSemester.value.nameEN
})

const hasEvents = computed(() => (dashboard.value?.totalEvents ?? 0) > 0)

const registrationStates = computed(
  () =>
    dashboard.value?.registrationStates ?? {
      open: 0,
      waitlist: 0,
      offer: 0,
      accepted: 0,
      declined: 0,
    },
)

const registrationStatesTotal = computed(
  () =>
    registrationStates.value.open +
    registrationStates.value.waitlist +
    registrationStates.value.offer +
    registrationStates.value.accepted +
    registrationStates.value.declined,
)

const topEvents = computed(() => dashboard.value?.topEvents ?? [])
const upcomingDeadlines = computed<PersonalDashboardDeadline[]>(
  () => dashboard.value?.upcomingDeadlines ?? [],
)
const recentActivities = ref<PersonalDashboardActivity[]>([])
const isLoadingActivities = ref(false)

function formatNumber(value?: number | null): string {
  if (value === null || value === undefined) {
    return '0'
  }

  return numberFormatter.value.format(value)
}

function formatDelta(value: number): string {
  if (value === 0) {
    return numberFormatter.value.format(0)
  }

  const sign = value > 0 ? '+' : '-'
  return `${sign}${numberFormatter.value.format(Math.abs(value))}`
}

function getEventName(
  item: { nameDE: string; nameEN?: string } | { eventNameDE: string; eventNameEN?: string },
) {
  if ('nameDE' in item) {
    return locale.value.startsWith('de') ? item.nameDE : item.nameEN || item.nameDE
  }

  return locale.value.startsWith('de') ? item.eventNameDE : item.eventNameEN || item.eventNameDE
}

function getEventOverviewLink(eventId: number): string {
  return `/manage/events/${eventId}/overview`
}

function getDeadlineTypeLabel(type: string): string {
  const key = `pages.dashboard.deadlines.types.${type}`
  const translated = t(key)
  return translated === key ? type : translated
}

function getDeadlineSeverity(type: string): 'success' | 'warn' {
  return type === 'registrationStart' ? 'success' : 'warn'
}

function parseActivityParams(params?: string): Record<string, unknown> | undefined {
  if (!params) {
    return undefined
  }

  try {
    const parsed = JSON.parse(params) as Record<string, unknown>
    const normalizeStatus = (value: unknown): unknown => {
      if (typeof value !== 'string' || value.length === 0) {
        return value
      }

      const key = value.charAt(0).toLowerCase() + value.slice(1)
      const translationKey = `enums.registrationStatus.${key}`
      const translated = t(translationKey)
      return translated === translationKey ? value : translated
    }

    parsed.oldStatus = normalizeStatus(parsed.oldStatus)
    parsed.newStatus = normalizeStatus(parsed.newStatus)
    return parsed
  } catch {
    return undefined
  }
}

function getActivityLabel(activity: PersonalDashboardActivity): string {
  if (activity.descriptionKey) {
    const params = parseActivityParams(activity.descriptionParams)
    return params ? t(activity.descriptionKey, params) : t(activity.descriptionKey)
  }

  if (activity.description) {
    return activity.description
  }

  switch (activity.type) {
    case 'SignupAdd':
      return t('features.events.events.newRegistration')
    case 'SignupRemove':
      return t('features.events.events.registrationRemoved')
    case 'StatusChange':
      return t('features.events.events.statusChange')
    case 'BoostActivate':
      return t('features.events.activity.boostActivate')
    case 'BoostDeactivate':
      return t('features.events.activity.boostDeactivate')
    default:
      return activity.type
  }
}

function getActivityIcon(activity: PersonalDashboardActivity): { icon: string; color: string } {
  switch (activity.type) {
    case 'SignupAdd':
      return { icon: 'i-lucide-user-plus', color: 'text-emerald-700 dark:text-emerald-400' }
    case 'SignupRemove':
      return { icon: 'i-lucide-user-minus', color: 'text-amber-700 dark:text-amber-400' }
    case 'StatusChange':
      return { icon: 'i-lucide-refresh-cw', color: 'text-sky-700 dark:text-sky-400' }
    case 'BoostActivate':
      return { icon: 'i-lucide-star', color: 'text-emerald-700 dark:text-emerald-400' }
    case 'BoostDeactivate':
      return { icon: 'i-lucide-star', color: 'text-muted' }
    case 'EventAdd':
      return { icon: 'i-lucide-circle-plus', color: 'text-emerald-700 dark:text-emerald-400' }
    case 'EventEdit':
      return { icon: 'i-lucide-pencil', color: 'text-sky-700 dark:text-sky-400' }
    case 'EventDelete':
      return { icon: 'i-lucide-trash', color: 'text-rose-700 dark:text-rose-400' }
    default:
      return { icon: 'i-lucide-bell', color: 'text-muted' }
  }
}

const statCards = computed(() => {
  const cards = [
    {
      key: 'events',
      label: t('pages.dashboard.stats.totalEvents'),
      value: formatNumber(dashboard.value?.totalEvents),
      subLabel: t('pages.dashboard.stats.openEvents'),
      subValue: formatNumber(dashboard.value?.openEvents),
      icon: 'i-lucide-calendar',
    },
    {
      key: 'registrations',
      label: t('pages.dashboard.stats.totalRegistrations'),
      value: formatNumber(dashboard.value?.totalRegistrations),
      subLabel: t('pages.dashboard.stats.registrationsLast24Hours'),
      subValue: formatDelta(dashboard.value?.registrationsLast24Hours ?? 0),
      icon: 'i-lucide-users',
    },
    {
      key: 'accepted',
      label: t('pages.dashboard.stats.acceptedRegistrations'),
      value: formatNumber(dashboard.value?.acceptedRegistrations),
      subLabel: t('pages.dashboard.stats.pendingRegistrations'),
      subValue: formatNumber(dashboard.value?.pendingRegistrations),
      icon: 'i-lucide-circle-check',
    },
    {
      key: 'deadlines',
      label: t('pages.dashboard.stats.deadlinesSoon'),
      value: formatNumber(dashboard.value?.deadlinesSoon),
      subLabel: t('pages.dashboard.stats.openEvents'),
      subValue: formatNumber(dashboard.value?.openEvents),
      icon: 'i-lucide-clock',
    },
  ]

  const pendingChangeCount = dashboard.value?.pendingChangeCount ?? 0
  if (pendingChangeCount > 0) {
    cards.push({
      key: 'drafts',
      label: t('pages.dashboard.stats.unpublishedDrafts'),
      value: formatNumber(pendingChangeCount),
      subLabel: t('pages.dashboard.stats.imminentDraws'),
      subValue: formatNumber(dashboard.value?.imminentDraftEventCount),
      icon: 'i-lucide-file-pen',
    })
  }

  return cards
})

const chartData = computed<ChartData<'bar'>>(() => {
  void primary.value
  void isDarkMode.value

  const documentStyle = getComputedStyle(document.documentElement)
  return {
    labels: [
      t('pages.dashboard.chart.labels.open'),
      t('pages.dashboard.chart.labels.waitlist'),
      t('pages.dashboard.chart.labels.offer'),
      t('pages.dashboard.chart.labels.accepted'),
      t('pages.dashboard.chart.labels.declined'),
    ],
    datasets: [
      {
        label: t('pages.dashboard.chart.registrationsLabel'),
        data: [
          registrationStates.value.open,
          registrationStates.value.waitlist,
          registrationStates.value.offer,
          registrationStates.value.accepted,
          registrationStates.value.declined,
        ],
        backgroundColor: [
          documentStyle.getPropertyValue('--ui-color-neutral-400'),
          documentStyle.getPropertyValue('--ui-color-warning-400'),
          documentStyle.getPropertyValue('--ui-color-info-400'),
          documentStyle.getPropertyValue('--ui-color-success-400'),
          documentStyle.getPropertyValue('--ui-color-error-400'),
        ],
        borderRadius: 8,
      },
    ],
  }
})

const chartOptions = computed<ChartOptions<'bar'>>(() => {
  void primary.value
  void isDarkMode.value

  const documentStyle = getComputedStyle(document.documentElement)
  return {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<'bar'>) =>
            `${context.parsed.y ?? 0} ${t('pages.dashboard.chart.registrationsLabel')}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: 'transparent',
        },
        ticks: {
          color: documentStyle.getPropertyValue('--ui-bg-muted0'),
          maxRotation: 30,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: documentStyle.getPropertyValue('--ui-border'),
        },
        ticks: {
          color: documentStyle.getPropertyValue('--ui-bg-muted0'),
          precision: 0,
        },
      },
    },
  }
})

async function loadDashboard() {
  isLoading.value = true
  error.value = null

  try {
    dashboard.value = await eventService.getPersonalDashboard(selectedSemester.value?.id)
  } catch (err) {
    console.error('Failed to load personal dashboard:', err)
    error.value = t('pages.dashboard.states.loadError')
    dashboard.value = null
  } finally {
    isLoading.value = false
  }
}

watch(
  () => locale.value,
  () => {
    setPageTitle(t('pages.dashboard.title'))
  },
  { immediate: true },
)

// Activities load separately from the dashboard so a large list or a wide time range never
// delays the stats and charts. The counter drops responses of superseded requests.
let activitiesRequestId = 0

async function loadActivities() {
  const requestId = ++activitiesRequestId
  isLoadingActivities.value = true

  try {
    const activities = await eventService.getPersonalActivities(
      selectedSemester.value?.id,
      activityHours.value,
    )
    if (requestId === activitiesRequestId) {
      recentActivities.value = activities
    }
  } catch (err) {
    console.error('Failed to load personal activities:', err)
    if (requestId === activitiesRequestId) {
      recentActivities.value = []
    }
  } finally {
    if (requestId === activitiesRequestId) {
      isLoadingActivities.value = false
    }
  }
}

watch(
  () => selectedSemester.value?.id,
  () => {
    loadDashboard()
  },
)

watch([() => selectedSemester.value?.id, activityHours], () => {
  loadActivities()
})

onMounted(() => {
  enableSemesterSelector()
  loadDashboard()
  loadActivities()
})
</script>

<template>
  <div class="space-y-6">
    <section class="">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div class="space-y-2">
          <p class="text-sm font-medium tracking-[0.2em] text-[var(--app-text-primary)] uppercase">
            {{ semesterLabel }}
          </p>
          <div class="space-y-1">
            <h1 class="text-3xl font-semibold tracking-tight text-neutral-900 dark:text-white">
              {{ t('pages.dashboard.title') }}
            </h1>
            <p class="max-w-3xl text-sm text-neutral-600 dark:text-neutral-300">
              {{ t('pages.dashboard.subtitle') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <AppAlert v-if="error" color="error" :closable="false">
      {{ error }}
    </AppAlert>

    <template v-if="isLoading">
      <section class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div
          v-for="card in 4"
          :key="card"
          class="h-36 animate-pulse rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
        />
      </section>
      <section class="grid grid-cols-1 gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <div
          class="h-[22rem] animate-pulse rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
        />
        <div
          class="h-[22rem] animate-pulse rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
        />
      </section>
    </template>

    <template v-else-if="hasEvents">
      <section class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <article
          v-for="card in statCards"
          :key="card.key"
          class="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md dark:border-neutral-800 dark:bg-neutral-900"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="space-y-3">
              <p class="text-sm font-medium text-muted">
                {{ card.label }}
              </p>
              <p class="text-3xl font-semibold tracking-tight text-neutral-900 dark:text-white">
                {{ card.value }}
              </p>
              <div class="text-sm text-muted">
                <span class="font-medium text-neutral-700 dark:text-neutral-200">
                  {{ card.subValue }}
                </span>
                <span class="ml-2">{{ card.subLabel }}</span>
              </div>
            </div>
            <div
              class="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-xl text-primary-600 dark:bg-primary-950/40"
            >
              <UIcon :name="card.icon" />
            </div>
          </div>
        </article>
      </section>

      <section class="grid grid-cols-1 gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <article
          class="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
        >
          <div class="mb-5 flex items-center justify-between gap-3">
            <div>
              <h2 class="text-lg font-semibold text-neutral-900 dark:text-white">
                {{ t('pages.dashboard.chart.title') }}
              </h2>
              <p class="text-sm text-muted">
                {{ t('pages.dashboard.chart.subtitle') }}
              </p>
            </div>
            <UBadge :label="formatNumber(registrationStatesTotal)" color="neutral" />
          </div>

          <div v-if="registrationStatesTotal > 0" class="h-80">
            <Bar :data="chartData" :options="chartOptions" />
          </div>
          <div
            v-else
            class="flex h-32 items-center justify-center rounded-2xl border border-dashed border-neutral-300 text-sm text-muted sm:h-80 dark:border-neutral-700 dark:text-neutral-400"
          >
            {{ t('pages.dashboard.chart.empty') }}
          </div>
        </article>

        <article
          class="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
        >
          <div class="mb-5 flex items-center justify-between gap-3">
            <div>
              <h2 class="text-lg font-semibold text-neutral-900 dark:text-white">
                {{ t('pages.dashboard.deadlines.title') }}
              </h2>
              <p class="text-sm text-muted">
                {{ t('pages.dashboard.deadlines.subtitle') }}
              </p>
            </div>
            <UBadge
              :label="formatNumber(upcomingDeadlines.length)"
              :color="toAppColor(upcomingDeadlines.length > 0 ? 'warn' : 'secondary')"
            />
          </div>

          <div v-if="upcomingDeadlines.length > 0" class="space-y-3">
            <router-link
              v-for="deadline in upcomingDeadlines"
              :key="`${deadline.eventId}-${deadline.type}-${deadline.date}`"
              :to="getEventOverviewLink(deadline.eventId)"
              class="flex items-start justify-between gap-4 rounded-2xl border border-neutral-200 px-4 py-3 transition-colors hover:border-primary-300 hover:bg-primary-50/50 dark:border-neutral-800 dark:hover:border-primary-700 dark:hover:bg-primary-950/20"
            >
              <div class="min-w-0 space-y-2">
                <p class="truncate font-medium text-neutral-900 dark:text-white">
                  {{ getEventName(deadline) }}
                </p>
                <div class="flex flex-wrap items-center gap-2">
                  <UBadge
                    :label="getDeadlineTypeLabel(deadline.type)"
                    :color="toAppColor(getDeadlineSeverity(deadline.type))"
                  />
                  <span class="text-sm text-muted">
                    {{ d(new Date(deadline.date), 'datetime') }}
                  </span>
                </div>
              </div>
              <UIcon name="i-lucide-chevron-right" class="mt-1 text-dimmed" />
            </router-link>
          </div>
          <div
            v-else
            class="flex min-h-56 items-center justify-center rounded-2xl border border-dashed border-neutral-300 px-6 text-center text-sm text-muted dark:border-neutral-700 dark:text-neutral-400"
          >
            {{ t('pages.dashboard.deadlines.empty') }}
          </div>
        </article>
      </section>

      <section class="grid grid-cols-1 gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <article
          class="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
        >
          <div class="mb-5 flex items-center justify-between gap-3">
            <div>
              <h2 class="text-lg font-semibold text-neutral-900 dark:text-white">
                {{ t('pages.dashboard.topEvents.title') }}
              </h2>
              <p class="text-sm text-muted">
                {{ t('pages.dashboard.topEvents.subtitle') }}
              </p>
            </div>
            <UBadge :label="formatNumber(topEvents.length)" color="neutral" />
          </div>

          <div v-if="topEvents.length > 0" class="space-y-3">
            <router-link
              v-for="event in topEvents"
              :key="event.id"
              :to="getEventOverviewLink(event.id)"
              class="flex items-center justify-between gap-4 rounded-2xl border border-neutral-200 px-4 py-3 transition-colors hover:border-primary-300 hover:bg-primary-50/50 dark:border-neutral-800 dark:hover:border-primary-700 dark:hover:bg-primary-950/20"
            >
              <div class="min-w-0">
                <p class="truncate font-medium text-neutral-900 dark:text-white">
                  {{ getEventName(event) }}
                </p>
                <p class="text-sm text-muted">
                  {{
                    t('pages.dashboard.topEvents.registrations', {
                      count: formatNumber(event.registrationCount),
                    })
                  }}
                </p>
              </div>
              <div class="flex items-center gap-3">
                <span class="text-xl font-semibold tracking-tight text-neutral-900 dark:text-white">
                  {{ formatNumber(event.registrationCount) }}
                </span>
                <UIcon name="i-lucide-chevron-right" class="text-dimmed" />
              </div>
            </router-link>
          </div>
          <div
            v-else
            class="flex min-h-56 items-center justify-center rounded-2xl border border-dashed border-neutral-300 px-6 text-center text-sm text-muted dark:border-neutral-700 dark:text-neutral-400"
          >
            {{ t('pages.dashboard.topEvents.empty') }}
          </div>
        </article>

        <article
          class="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
        >
          <div class="mb-5 flex items-center justify-between gap-3">
            <div>
              <h2 class="text-lg font-semibold text-neutral-900 dark:text-white">
                {{ t('pages.dashboard.activity.title') }}
              </h2>
              <p class="text-sm text-muted">
                {{
                  activityRange === 'all'
                    ? t('pages.dashboard.activity.subtitleSemester')
                    : t('pages.dashboard.activity.subtitle', {
                        range: t(`common.activityRange.rangeNames.${activityRange}`),
                      })
                }}
              </p>
            </div>
            <div class="flex flex-wrap items-center justify-end gap-2">
              <UBadge :label="formatNumber(recentActivities.length)" color="neutral" />
              <ActivityRangeToggle
                v-model="activityRange"
                :all-label="t('pages.dashboard.activity.rangeSemester')"
              />
            </div>
          </div>

          <div v-if="recentActivities.length > 0" class="max-h-[36rem] space-y-3 overflow-y-auto">
            <router-link
              v-for="activity in recentActivities"
              :key="`${activity.eventId}-${activity.timestamp}-${activity.type}`"
              :to="getEventOverviewLink(activity.eventId)"
              class="flex items-start gap-4 rounded-2xl border border-neutral-200 px-4 py-3 transition-colors hover:border-primary-300 hover:bg-primary-50/50 dark:border-neutral-800 dark:hover:border-primary-700 dark:hover:bg-primary-950/20"
            >
              <div
                class="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-neutral-100 dark:bg-neutral-800"
              >
                <UIcon
                  :name="getActivityIcon(activity).icon"
                  class="text-base"
                  :class="getActivityIcon(activity).color"
                />
              </div>
              <div class="min-w-0 flex-1 space-y-1">
                <p class="text-sm leading-6 text-neutral-700 dark:text-neutral-200">
                  {{ getActivityLabel(activity) }}
                </p>
                <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
                  <span class="font-medium">{{ getEventName(activity) }}</span>
                  <span>{{ d(new Date(activity.timestamp), 'datetime') }}</span>
                </div>
              </div>
              <UIcon name="i-lucide-chevron-right" class="mt-1 text-dimmed" />
            </router-link>
          </div>
          <div
            v-else
            class="flex min-h-56 items-center justify-center rounded-2xl border border-dashed border-neutral-300 px-6 text-center text-sm text-muted dark:border-neutral-700 dark:text-neutral-400"
          >
            {{
              isLoadingActivities
                ? t('common.labels.loading')
                : activityRange === 'all'
                  ? t('pages.dashboard.activity.emptySemester')
                  : t('pages.dashboard.activity.empty', {
                      range: t(`common.activityRange.rangeNames.${activityRange}`),
                    })
            }}
          </div>
        </article>
      </section>
    </template>

    <section
      v-else
      class="rounded-3xl border border-dashed border-neutral-300 bg-white px-6 py-14 text-center shadow-sm dark:border-neutral-700 dark:bg-neutral-900"
    >
      <div class="mx-auto max-w-2xl space-y-4">
        <div
          class="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-primary-50 text-3xl text-primary-600 dark:bg-primary-950/40"
        >
          <UIcon name="i-lucide-chart-line" />
        </div>
        <div class="space-y-2">
          <h2 class="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-white">
            {{ t('pages.dashboard.empty.title') }}
          </h2>
          <p class="text-sm text-neutral-600 dark:text-neutral-300">
            {{ t('pages.dashboard.empty.subtitle', { semester: semesterLabel }) }}
          </p>
        </div>
        <div class="pt-2">
          <router-link
            to="/manage/events"
            class="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-inverted transition-opacity hover:opacity-90"
          >
            <UIcon name="i-lucide-arrow-right" />
            <span>{{ t('pages.dashboard.empty.action') }}</span>
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>
